import { ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { usePermissions } from '../hooks/usePermissions'

interface PermissionGuardProps {
  children: ReactNode
  permissions?: string[]
  requireAll?: boolean
  fallbackPath?: string
  fallbackComponent?: ReactNode
}

const PermissionGuard = ({
  children,
  permissions = [],
  requireAll = false,
  // '/' renders HomeRedirect, which resolves to /dashboard for everyone except
  // dashboard-less roles (DeliveryDriver → /driver). Bouncing straight to
  // /dashboard would strand a driver on a page whose every query errors.
  fallbackPath = '/',
  fallbackComponent
}: PermissionGuardProps) => {
  // Live permissions (with login-snapshot fallback) — the same source the
  // sidebar and the pages use. Checking only the localStorage snapshot here
  // locked out any permission granted after login until the next re-login.
  const { hasAnyPermission, hasAllPermissions, permissionsLoading, apiPermissions } = usePermissions()

  // If no permissions specified, allow access
  if (permissions.length === 0) {
    return <>{children}</>
  }

  // Check permissions
  const hasAccess = requireAll
    ? hasAllPermissions(permissions)
    : hasAnyPermission(permissions)

  if (!hasAccess) {
    // Denied by the stale fallback while the live query is still in flight —
    // hold off rather than redirect on a snapshot that may be missing a
    // freshly seeded permission.
    if (permissionsLoading && apiPermissions.length === 0) {
      return null
    }
    if (fallbackComponent) {
      return <>{fallbackComponent}</>
    }
    return <Navigate to={fallbackPath} replace />
  }

  return <>{children}</>
}

export default PermissionGuard
